import { useState, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Alert, Box, Button, IconButton, paletteColors, Typography } from '@evaneos/b2b-ui';
import { alpha } from '@evaneos/b2b-ui';

import PlayArrowIcon from '@evaneos/b2b-ui/icons/PlayArrow';
import PhoneIcon from '@evaneos/b2b-ui/icons/LocalPhone';

import { deprecated_000000 } from '../../../styles/deprecated-colors';

import { usePhoneCall } from '../../../shared/providers/PhoneCallContext';
import { marketCodeToLanguage } from '../../../resources/phoning';
import { useGetDossierData } from '../../../resources/dossier/queries';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../shared/analytics';
import { useHumanizeTwilioError } from '../../../resources/phoning/useHumanizeTwilioError';

import callDemoVideo from './../../../assets/call_demo_video.mp4';
import callDemoPoster from './../../../assets/call_demo_poster.jpg';

/* eslint-disable max-lines-per-function -- onboarding screen with video, error handling and call initiation */
export const InternetCallOnboarding = (): JSX.Element => {
    const { formatMessage } = useIntl();
    const { state, startCall } = usePhoneCall();
    const humanizeTwilioError = useHumanizeTwilioError();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const { marketCode, requesterName } =
        useGetDossierData(state.dossierId!)?.dossier?.project || {};

    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(state.dossierId!);

    const errorMessage = state.error ? humanizeTwilioError(state.error) : null;

    const handleStartCall = () => {
        if (!marketCode || !state.dossierId || !requesterName) return;

        const language = marketCodeToLanguage(marketCode);
        startCall(state.dossierId, requesterName, language);

        trackEvent((ampli, defaultProperties) => {
            ampli.travelerCallStarted({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    const handleToggleVideo = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                void videoRef.current.play();
            }
        }
    };

    const handleVideoPlay = () => {
        setIsPlaying(true);
    };

    const handleVideoPause = () => {
        setIsPlaying(false);
    };

    const isStarting = state.status === 'starting';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 3,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="titleMoretL"
                        sx={{
                            marginBottom: 0.5,
                            marginRight: { xs: 4, md: 0 },
                        }}
                    >
                        {formatMessage({ id: 'phoning.internet_call_onboarding.title' })}
                    </Typography>

                    <Typography variant="body2" sx={{ marginBottom: 3, color: 'text.secondary' }}>
                        {formatMessage({
                            id: 'phoning.internet_call_onboarding.check_network_connection',
                        })}
                    </Typography>

                    {errorMessage && (
                        <Alert
                            severity="error"
                            data-testid="phoning-error-alert"
                            sx={{ marginBottom: 3 }}
                        >
                            <Typography variant="body2" color="currentColor">
                                {errorMessage}
                            </Typography>
                        </Alert>
                    )}

                    <Box sx={{ p: 2, backgroundColor: paletteColors.neutral[100] }}>
                        <Typography
                            component="h2"
                            variant="h4"
                            sx={{ marginBottom: 1, fontSize: '1.2rem' }}
                        >
                            {formatMessage({ id: 'phoning.internet_call_onboarding.tips.title' })}
                        </Typography>

                        <Box
                            component="ol"
                            sx={{
                                listStyle: 'desc',
                                listStylePosition: 'inside',
                                paddingLeft: 0,
                                margin: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                            }}
                        >
                            <Typography variant="body2" component="li">
                                {formatMessage({
                                    id: 'phoning.internet_call_onboarding.tips.start_call',
                                })}
                            </Typography>
                            <Typography variant="body2" component="li">
                                {formatMessage({
                                    id: 'phoning.internet_call_onboarding.tips.save_call',
                                })}
                            </Typography>
                            <Typography variant="body2" component="li">
                                {formatMessage({
                                    id: 'phoning.internet_call_onboarding.tips.consult_summary',
                                })}
                            </Typography>
                        </Box>
                    </Box>

                    <Box marginTop={3} marginBottom={2}>
                        <Typography
                            component="h2"
                            variant="h4"
                            sx={{ marginBottom: 0.5, fontSize: '1.2rem' }}
                        >
                            {formatMessage({
                                id: 'phoning.internet_call_onboarding.did_you_know.title',
                            })}
                        </Typography>
                        <Typography variant="body2">
                            {formatMessage(
                                {
                                    id: 'phoning.internet_call_onboarding.did_you_know.success_rate',
                                },
                                {
                                    percentage: '96%',
                                    strong: (chunks: React.ReactNode) => (
                                        <Typography
                                            component="strong"
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {chunks}
                                        </Typography>
                                    ),
                                },
                            )}
                        </Typography>
                    </Box>

                    <Button
                        data-testid="start-phoning-button"
                        fullWidth
                        onClick={handleStartCall}
                        disabled={isStarting && !state.error}
                        startIcon={<PhoneIcon />}
                    >
                        {isStarting && !state.error
                            ? formatMessage({ id: 'phoning.calling' })
                            : formatMessage({ id: 'phoning.start-internet-to-phone-call' })}
                    </Button>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        mt: 4,
                        display: { xs: 'none', md: 'block' },
                        backgroundColor: paletteColors.neutral[900],
                        minHeight: { xs: '200px', md: '300px' },
                    }}
                >
                    <Box
                        component="video"
                        ref={videoRef}
                        preload="none"
                        src={callDemoVideo}
                        poster={callDemoPoster}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: 'pointer',
                        }}
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onEnded={handleVideoPause}
                        onClick={handleToggleVideo}
                    />

                    {!isPlaying && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: alpha(deprecated_000000, 0.3),
                                cursor: 'pointer',
                                pointerEvents: 'none',
                            }}
                        >
                            <IconButton
                                sx={{
                                    backgroundColor: paletteColors.neutral[0],
                                    width: '64px',
                                    height: '64px',
                                    '&:hover': {
                                        backgroundColor: paletteColors.neutral[0],
                                    },
                                }}
                                aria-label={formatMessage({
                                    id: 'dossier.discussion.phone_call.audio_player.play_button_label',
                                })}
                            >
                                <PlayArrowIcon
                                    sx={{ fontSize: '40px', color: paletteColors.neutral[900] }}
                                />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
/* eslint-enable max-lines-per-function */
